import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Copy, KeyRound, Plus } from 'lucide-react';
import {
  API_KEY_SCOPES,
  API_KEY_SCOPE_LABELS,
  type ApiKey,
  type ApiKeyCreated,
  type ApiKeyScope,
  type Paginated,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { formatDateTime, formatRelative } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField } from '@/components/ContextPanel';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { DateInput } from '@/components/DateInput';

/**
 * Chaves de API.
 *
 * É por aqui que o site amaipark.com vai consumir a plataforma (Day Use, leads,
 * disponibilidade, conteúdo). O segredo aparece uma única vez, na criação — o
 * banco guarda só o hash.
 */
export function ApiKeysPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const query = useQuery({
    queryKey: ['api-keys', page],
    queryFn: () => api.get<Paginated<ApiKey>>('/api-keys', { page, perPage: 25 }),
  });

  const selected = query.data?.data.find((row) => row.id === selectedId) ?? null;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['api-keys'] });
  };

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.post<ApiKey>(`/api-keys/${id}/revoke`, {}),
    onSuccess: () => {
      setRevoking(false);
      refresh();
    },
  });

  const statusOf = (key: ApiKey) => {
    if (key.revokedAt) return <StatusBadge kind="bad">Revogada</StatusBadge>;
    if (key.expiresAt && new Date(key.expiresAt) < new Date())
      return <StatusBadge kind="warn">Expirada</StatusBadge>;
    if (!key.isActive) return <StatusBadge kind="mute">Inativa</StatusBadge>;
    return <StatusBadge kind="ok">Ativa</StatusBadge>;
  };

  const columns: Array<Column<ApiKey>> = [
    { key: 'name', label: 'Chave', strong: true, render: (key) => key.name },
    { key: 'prefix', label: 'Prefixo', mono: true, render: (key) => `amai_${key.prefix}…` },
    {
      key: 'scopes',
      label: 'Escopos',
      render: (key) =>
        key.scopes.map((scope) => API_KEY_SCOPE_LABELS[scope as ApiKeyScope] ?? scope).join(' · '),
    },
    {
      key: 'lastUsedAt',
      label: 'Último uso',
      render: (key) => (key.lastUsedAt ? formatRelative(key.lastUsedAt) : 'nunca usada'),
    },
    { key: 'status', label: 'Situação', render: statusOf },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Chaves de API"
        actions={
          can('api-keys:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Emitir chave
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        <div className="note note--top">
          <KeyRound size={16} className="note__icon" aria-hidden />
          <div>
            O segredo completo aparece uma única vez, na emissão. Perdeu? Revogue e emita outra.
          </div>
        </div>

        {query.isPending && <TableSkeleton columns={5} />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}
        {query.data && (
          <SmartDataTable
            caption="Chaves de API emitidas"
            columns={columns}
            rows={query.data.data}
            meta={query.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            emptyState={
              <EmptyState
                title="Nenhuma chave emitida"
                description="Emita uma chave quando for conectar o site à plataforma."
                action={
                  can('api-keys:criar') ? (
                    <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                      Emitir chave
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name ?? ''}
        subtitle={selected ? `amai_${selected.prefix}…` : undefined}
        footer={
          selected && !selected.revokedAt && can('api-keys:editar') ? (
            <Button small variant="danger" icon={Ban} onClick={() => setRevoking(true)}>
              Revogar
            </Button>
          ) : undefined
        }
      >
        {!selected ? null : (
          <>
            <ReadField label="Situação" value={statusOf(selected)} />
            <ReadField
              label="Escopos"
              value={selected.scopes
                .map((scope) => API_KEY_SCOPE_LABELS[scope as ApiKeyScope] ?? scope)
                .join('\n')}
            />
            <ReadField
              label="Último uso"
              value={selected.lastUsedAt ? formatDateTime(selected.lastUsedAt) : 'Nunca usada'}
              mono
            />
            <ReadField
              label="Expira em"
              value={selected.expiresAt ? formatDateTime(selected.expiresAt) : 'Sem prazo'}
              mono
            />
            <ReadField
              label="Revogada em"
              value={selected.revokedAt ? formatDateTime(selected.revokedAt) : '—'}
              mono
            />
            <ReadField label="Criada em" value={formatDateTime(selected.createdAt)} mono />
            <p className="muted" style={{ fontSize: 12.5, marginTop: 18 }}>
              O segredo completo é mostrado apenas no momento da criação. Se ele foi perdido,
              revogue esta chave e emita outra.
            </p>
          </>
        )}
      </ContextPanel>

      <CreateKeyModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          refresh();
        }}
      />

      <ConfirmDialog
        open={revoking}
        destructive
        loading={revokeMutation.isPending}
        title={selected ? `Revogar ${selected.name}?` : ''}
        confirmLabel="Revogar chave"
        message={
          <>
            Quem usa esta chave para de acessar NA HORA e não há como reativá-la —
            só emitindo uma nova.
            {revokeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(revokeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRevoking(false);
          revokeMutation.reset();
        }}
        onConfirm={() => selected && revokeMutation.mutate(selected.id)}
      />
    </>
  );
}

function CreateKeyModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>([]);
  const [expiresAt, setExpiresAt] = useState('');
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const [copied, setCopied] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName('');
      setScopes([]);
      setExpiresAt('');
      setCreated(null);
      setCopied(false);
    }
  }

  const mutation = useMutation({
    mutationFn: () =>
      api.post<ApiKeyCreated>('/api-keys', {
        name: name.trim(),
        scopes,
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
      }),
    onSuccess: (result) => {
      setCreated(result);
      onCreated();
    },
  });

  const errors = fieldErrors(mutation.error);
  const toggleScope = (scope: ApiKeyScope) =>
    setScopes((current) =>
      current.includes(scope) ? current.filter((v) => v !== scope) : [...current, scope],
    );

  const copySecret = () => {
    if (!created) return;
    void navigator.clipboard.writeText(created.secret).then(() => setCopied(true));
  };

  // Depois de criada, o modal vira a ÚNICA chance de copiar o segredo.
  if (created) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="Chave emitida — copie o segredo agora"
        description="Esta é a única vez que ele aparece; o banco guarda apenas o hash"
        footer={
          <Button variant="primary" onClick={onClose}>
            Já copiei, fechar
          </Button>
        }
      >
        <FormSection>
          <Field label="Segredo da chave" htmlFor="ak-secret">
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                id="ak-secret"
                className="input input--mono"
                readOnly
                value={created.secret}
                onFocus={(e) => e.target.select()}
                style={{ flex: 1 }}
              />
              <Button icon={Copy} onClick={copySecret}>
                {copied ? 'Copiado!' : 'Copiar'}
              </Button>
            </div>
          </Field>
        </FormSection>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Emitir chave de API"
      description="Dê o mínimo de escopos que a integração precisa"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            icon={KeyRound}
            loading={mutation.isPending}
            disabled={name.trim().length < 2 || scopes.length === 0}
            onClick={() => mutation.mutate()}
          >
            Emitir
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}
      <FormSection>
        <Field label="Nome da chave" htmlFor="ak-name" error={errors.name} hint="Quem vai usar, ex.: Site amaipark.com">
          <input id="ak-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        {/*
          Escopo é escolha MÚLTIPLA: um `group`, não um `radiogroup`.

          Cada caixa já vinha com o `<label>` dela, então o controle tinha
          nome. O que não tinha nome era o CONJUNTO: quem usa leitor de tela
          ouvia as opções soltas, sem saber que pertencem a "Escopos" nem
          quantas são. O `<Field>` não amarra por `htmlFor` porque não há um
          campo só para apontar.
        */}
        <Field label="Escopos" error={errors.scopes}>
          <div role="group" aria-label="Escopos" style={{ display: 'grid', gap: 6 }}>
            {API_KEY_SCOPES.map((scope) => (
              <label key={scope} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={scopes.includes(scope)}
                  onChange={() => toggleScope(scope)}
                />
                {API_KEY_SCOPE_LABELS[scope]}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Expira em (opcional)" htmlFor="ak-expires">
          <DateInput
            id="ak-expires"
            className="input input--mono"
            value={expiresAt}
            onChange={(iso) => setExpiresAt(iso)}
          />
        </Field>
      </FormSection>
    </Modal>
  );
}
